import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ExternalLink, Star, Bell, ChevronDown, ChevronUp, Ruler } from 'lucide-react';
import { getProduct, getReviews, createReview, addToWishlist, removeFromWishlist, createPriceAlert, getSizeGuide } from '../api';
import { trackAffiliateClick } from '../api/admin';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const ProductDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [sizeGuide, setSizeGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [priceAlertTarget, setPriceAlertTarget] = useState('');
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productRes, reviewsRes] = await Promise.all([
          getProduct(id),
          getReviews(id)
        ]);
        setProduct(productRes.data);
        setReviews(reviewsRes.data);
        if (productRes.data.colors?.length > 0) {
          setSelectedColor(productRes.data.colors[0]);
        }
        
        // Fetch size guide
        try {
          const guideRes = await getSizeGuide(productRes.data.category);
          setSizeGuide(guideRes.data);
        } catch (e) {
          // Size guide might not exist for this category
        }
      } catch (error) {
        console.error('Failed to fetch product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleWishlistToggle = async () => {
    if (!user) {
      toast.error('Please login to add to wishlist');
      return;
    }
    try {
      if (isWishlisted) {
        await removeFromWishlist(product.id);
        toast.success('Removed from wishlist');
      } else {
        await addToWishlist(product.id);
        toast.success('Added to wishlist');
      }
      setIsWishlisted(!isWishlisted);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update wishlist');
    }
  };

  const handleSubmitReview = async () => {
    if (!user) {
      toast.error('Please login to review');
      return;
    }
    try {
      const response = await createReview({
        product_id: product.id,
        rating: reviewRating,
        comment: reviewComment
      });
      setReviews([response.data, ...reviews]);
      setShowReviewForm(false);
      setReviewComment('');
      toast.success('Review submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const handleCreatePriceAlert = async () => {
    if (!user) {
      toast.error('Please login to set price alerts');
      return;
    }
    if (!priceAlertTarget || parseFloat(priceAlertTarget) >= product.price) {
      toast.error('Target price must be lower than current price');
      return;
    }
    try {
      await createPriceAlert({
        product_id: product.id,
        target_price: parseFloat(priceAlertTarget)
      });
      toast.success('Price alert created!');
      setPriceAlertTarget('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create alert');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D2FF00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl mb-4">PRODUCT NOT FOUND</h1>
          <Link to="/shop" className="btn-primary inline-block">
            BACK TO SHOP
          </Link>
        </div>
      </div>
    );
  }

  const discount = product.original_price > product.price 
    ? Math.round((1 - product.price / product.original_price) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-6 lg:px-12" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm">
          <Link to="/" className="text-white/40 hover:text-white">Home</Link>
          <span className="text-white/40 mx-2">/</span>
          <Link to="/shop" className="text-white/40 hover:text-white">Shop</Link>
          <span className="text-white/40 mx-2">/</span>
          <span className="text-white">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
          >
            <div className="aspect-square bg-[#121212] overflow-hidden">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            </div>
            
            {/* Tags */}
            <div className="absolute top-4 left-4 flex gap-2">
              {product.is_hot && <span className="tag-hot">HOT</span>}
              {product.is_limited && <span className="tag-limited">LIMITED</span>}
              {discount > 0 && <span className="bg-white text-black text-xs font-bold px-2 py-1">-{discount}%</span>}
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <p className="text-[#D2FF00] text-sm font-bold uppercase tracking-wider mb-2">{product.brand}</p>
            <h1 className="font-display text-4xl sm:text-5xl uppercase mb-4" data-testid="product-name">{product.name}</h1>
            
            {/* Rating */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={18}
                    className={i < Math.floor(product.rating) ? 'star-filled fill-current' : 'star-empty'}
                  />
                ))}
              </div>
              <span className="text-white/60">({product.review_count} reviews)</span>
            </div>

            {/* Price */}
            <div className="flex items-center gap-4 mb-8">
              <span className="price-current text-4xl" data-testid="product-price">${product.price.toFixed(2)}</span>
              {discount > 0 && (
                <span className="price-original text-xl">${product.original_price.toFixed(2)}</span>
              )}
            </div>

            <p className="text-white/60 mb-8 leading-relaxed">{product.description}</p>

            {/* Color Selection */}
            {product.colors?.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-medium mb-3">COLOR: <span className="text-white/60">{selectedColor}</span></p>
                <div className="flex gap-2 flex-wrap">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 border transition-all text-sm ${
                        selectedColor === color
                          ? 'border-[#D2FF00] text-[#D2FF00]'
                          : 'border-white/20 text-white/60 hover:border-white/40'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            {product.sizes?.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium">SIZE: <span className="text-white/60">{selectedSize || 'Select'}</span></p>
                  {sizeGuide && (
                    <Dialog open={showSizeGuide} onOpenChange={setShowSizeGuide}>
                      <DialogTrigger asChild>
                        <button className="text-[#D2FF00] text-sm flex items-center gap-1 hover:underline">
                          <Ruler size={14} />
                          Size Guide
                        </button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl">
                        <DialogHeader>
                          <DialogTitle className="font-display text-2xl">SIZE GUIDE</DialogTitle>
                        </DialogHeader>
                        <div className="overflow-x-auto mt-4">
                          {product.category === 'sneakers' && sizeGuide.US && (
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-white/10">
                                  <th className="text-left py-2 text-white/60">US</th>
                                  <th className="text-left py-2 text-white/60">EU</th>
                                  <th className="text-left py-2 text-white/60">UK</th>
                                  <th className="text-left py-2 text-white/60">CM</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sizeGuide.US.map((_, i) => (
                                  <tr key={i} className="border-b border-white/5">
                                    <td className="py-2">{sizeGuide.US[i]}</td>
                                    <td className="py-2">{sizeGuide.EU[i]}</td>
                                    <td className="py-2">{sizeGuide.UK[i]}</td>
                                    <td className="py-2">{sizeGuide.CM[i]}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`size-option ${selectedSize === size ? 'selected' : ''}`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4 mb-8">
              <a
                href={product.affiliate_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackAffiliateClick(product.id).catch(() => {})}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="buy-now-btn"
              >
                <ExternalLink size={18} />
                BUY FROM {product.brand.toUpperCase()}
              </a>
              <Button
                variant="outline"
                className={`btn-secondary px-4 ${isWishlisted ? 'border-[#FF3B30] text-[#FF3B30]' : ''}`}
                onClick={handleWishlistToggle}
                data-testid="wishlist-btn"
              >
                <Heart size={20} fill={isWishlisted ? '#FF3B30' : 'none'} />
              </Button>
            </div>

            {/* Price Alert */}
            <div className="bg-[#0A0A0A] p-6 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Bell size={18} className="text-[#D2FF00]" />
                <h3 className="font-display text-lg">SET PRICE ALERT</h3>
              </div>
              <div className="flex gap-3">
                <input
                  type="number"
                  placeholder="Target price"
                  value={priceAlertTarget}
                  onChange={(e) => setPriceAlertTarget(e.target.value)}
                  className="input-default flex-1"
                  data-testid="price-alert-input"
                />
                <Button onClick={handleCreatePriceAlert} className="btn-primary" data-testid="set-alert-btn">
                  SET ALERT
                </Button>
              </div>
            </div>

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/shop?search=${tag}`}
                    className="text-xs text-white/40 hover:text-[#D2FF00] uppercase"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Reviews Section */}
        <section className="mt-24">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-display text-3xl uppercase">REVIEWS ({reviews.length})</h2>
            {user && (
              <Button
                onClick={() => setShowReviewForm(!showReviewForm)}
                className="btn-secondary py-2 px-4"
                data-testid="write-review-btn"
              >
                {showReviewForm ? 'CANCEL' : 'WRITE REVIEW'}
              </Button>
            )}
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#0A0A0A] p-6 mb-8"
            >
              <div className="mb-4">
                <p className="text-sm mb-2">Rating</p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      className="p-1"
                    >
                      <Star
                        size={24}
                        className={star <= reviewRating ? 'star-filled fill-current' : 'star-empty'}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <Textarea
                placeholder="Write your review..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                className="input-default min-h-[120px] mb-4"
                data-testid="review-textarea"
              />
              <Button onClick={handleSubmitReview} className="btn-primary" data-testid="submit-review-btn">
                SUBMIT REVIEW
              </Button>
            </motion.div>
          )}

          {/* Reviews List */}
          <div className="space-y-6">
            {reviews.length === 0 ? (
              <p className="text-white/60 text-center py-12">No reviews yet. Be the first to review!</p>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className="bg-[#0A0A0A] p-6 border-l-2 border-[#D2FF00]">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium">{review.user_name}</p>
                      <div className="flex gap-0.5 mt-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={14}
                            className={i < review.rating ? 'star-filled fill-current' : 'star-empty'}
                          />
                        ))}
                      </div>
                    </div>
                    <span className="text-white/40 text-sm">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-white/80">{review.comment}</p>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProductDetail;
